import { notify } from '@/atoms/Notifications'
import { convertDenomToMicroDenom, strideAccountAtom } from '@/atoms/Wallet'
import { STRIDE_CURRENCY } from '@/config'
import { useEligibleAirdropsQuery } from '@/queries'
import { queryKeys } from '@/query-keys'
import { fatal } from '@/utils'
import { assertIsDeliverTxSuccess } from '@cosmjs/stargate'
import { stride } from 'stridejs'
import BigNumber from 'bignumber.js'
import { useAtomValue } from 'jotai'
import { useMutation, useQueryClient } from 'react-query'

const useFreeClaimMutation = () => {
  const strideAccount = useAtomValue(strideAccountAtom)

  const client = useQueryClient()

  const { data: eligibleAirdrops } = useEligibleAirdropsQuery()

  const handleMutation = async () => {
    if (!strideAccount) {
      return
    }

    if (!eligibleAirdrops) {
      // Likely we forgot to load eligible airdrops from the airdrop root component.
      // In addition, while eligible airdrops are being loaded or have resulted to an error, let's disable any button that
      // leads them to execute this code.
      throw fatal('Unable to calculate gas multiplier as eligible airdrops have not been fetched yet.')
    }

    const gasMultiplier = Math.max(eligibleAirdrops.length, 1)

    const gasAmount = new BigNumber(0.35).multipliedBy(gasMultiplier)

    const msgClaimFreeAmount = stride.claim.MessageComposer.withTypeUrl.claimFreeAmount({
      user: strideAccount.key.bech32Address
    })

    const fee = {
      amount: [
        {
          amount: '0',
          denom: strideAccount.currency.coinDenom
        }
      ],
      gas: String(convertDenomToMicroDenom(gasAmount))
    }

    const tx = await strideAccount.client.signAndBroadcast(
      strideAccount.key.bech32Address,
      [msgClaimFreeAmount],
      fee,
      ''
    )

    assertIsDeliverTxSuccess(tx)

    return tx
  }

  const handleSuccess = async () => {
    await client.invalidateQueries(queryKeys.airdropClaimRecordBase)
    await client.invalidateQueries(queryKeys.airdropTotalClaimableBase)
    await client.invalidateQueries(queryKeys.airdropClaimHistoryBase)
    notify.success(`You have claimed your free ${STRIDE_CURRENCY.coinDenom} tokens successfully.`)
  }

  const handleError = () => {
    notify.error(`An error occurred while claiming your free ${STRIDE_CURRENCY.coinDenom} tokens. Please try again.`)
  }

  return useMutation(handleMutation, { onSuccess: handleSuccess, onError: handleError })
}

export { useFreeClaimMutation }
